import { blockOf, isBlock, parse as kqlParse, serialize as kqlSerialize } from './kql';

/**
 * Rules that watch a repeating group's ENTRIES (keepr-api KPR-687,
 * `trigger.type: 'entry-event'`): the client's reading of the stored shape.
 *
 *   trigger: { type: 'entry-event', card_id, group, events: ['added'|'changed'|'removed', …],
 *              columnsChanged?, includeDerived?, entryCondition?: { kql, evaluateOn? }, condition? }
 *
 * The API stores the entry condition as ONE KQL block on the group, its group
 * and columns by id (`#g has (#c = true)`); people write and read its inside
 * (`done = true`). Run rows stamp `trigger: { type: 'entry-event', event,
 * group, entries: [{ id, op, position }], entriesMore? }`.
 */

export const ENTRY_EVENT = 'entry-event';
export const ENTRY_EVENTS = [ 'added', 'changed', 'removed' ];

const labelOf = el => el?.label?.singular || el?.name || '';

export const isEntryRule = rule => rule?.trigger?.type === ENTRY_EVENT;

/** A group's columns as a card serves them (options.columns, or a schema read's flat `columns`). */
export function columnsOfGroup( group ) {
	const list = Array.isArray( group?.options?.columns ) ? group.options.columns : ( Array.isArray( group?.columns ) ? group.columns : [] );
	return list.filter( col => col?.name );
}

/** The group a rule watches, on a card's merged elements (the last of a name wins). */
export function groupOf( elements, name ) {
	let found = null;
	( elements || [] ).forEach( el => { if ( el?.name === name ) found = el; } );
	return found && found.dataType === 'repeating-group' ? found : null;
}

/**
 * The INSIDE of the stored block (`#g has (#c = true)` → `#c = true`), which
 * the condition builder edits over the group's columns. Text that is not one
 * block is handed back as it is.
 */
export function entryConditionInside( stored ) {
	const text = ( stored || '' ).trim();
	if ( ! text ) return '';
	try {
		const ast = kqlParse( text );
		return isBlock( ast ) ? kqlSerialize( blockOf( ast ) ) : text;
	} catch ( e ) {
		return text;
	}
}

/**
 * The entry condition as a person reads it: the inside of the block, each
 * column named by its label rather than its id. A column the group no longer
 * declares keeps its `#id`.
 */
export function entryConditionWords( stored, group ) {
	const inside = entryConditionInside( stored );
	if ( ! inside ) return '';
	const byId = new Map( columnsOfGroup( group ).filter( col => col.id ).map( col => [ `#${ col.id }`, col.name ] ) );
	if ( ! byId.size ) return inside;
	try {
		const named = node => {
			if ( ! node || typeof node !== 'object' ) return node;
			if ( node.op === 'and' || node.op === 'or' ) return { ...node, args: ( node.args || [] ).map( named ) };
			if ( node.op === 'not' ) return { ...node, arg: named( node.arg ) };
			if ( node.op === 'cmp' && Array.isArray( node.path ) && byId.has( `${ node.path[ 0 ] }` ) ) {
				return { ...node, path: [ byId.get( `${ node.path[ 0 ] }` ), ...node.path.slice( 1 ) ] };
			}
			return node;
		};
		return kqlSerialize( named( kqlParse( inside ) ) );
	} catch ( e ) {
		return inside;
	}
}

const joinOr = words => {
	const parts = words.filter( Boolean );
	if ( parts.length <= 1 ) return parts[ 0 ] || '';
	return `${ parts.slice( 0, -1 ).join( ', ' ) } or ${ parts[ parts.length - 1 ] }`;
};

/**
 * One entry rule's trigger, in a sentence: which entries, of which group, on
 * which card; the columns whose change sets it off; the entry's condition and
 * the record's. `card` is the trigger card's definition (its merged elements
 * name the group and its columns); without it the stored names stand in.
 */
export function describeEntryTrigger( rule, card = null ) {
	const trigger = rule?.trigger || {};
	const group = groupOf( card?.elements, trigger.group );
	const groupName = labelOf( group ) || trigger.group || 'a group';
	const subject = card?.name ? `a ${ card.name }` : 'an item';
	const events = Array.isArray( trigger.events ) && trigger.events.length ? trigger.events : ENTRY_EVENTS;
	let text = `When an entry of ${ groupName } on ${ subject } is ${ joinOr( events ) }`;
	const columns = columnsOfGroup( group );
	const columnLabel = name => labelOf( columns.find( col => col.name === name ) ) || name;
	if ( events.includes( 'changed' ) && Array.isArray( trigger.columnsChanged ) && trigger.columnsChanged.length ) {
		text += ` (${ joinOr( trigger.columnsChanged.map( columnLabel ) ) } changed)`;
	}
	const entryKql = trigger.entryCondition?.kql ? entryConditionWords( trigger.entryCondition.kql, group ) : '';
	if ( entryKql ) text += ` — if the entry: ${ entryKql }`;
	const kql = trigger.condition?.kql;
	if ( kql ) text += ` — if the item: ${ kql }`;
	if ( trigger.condition?.related?.length ) text += ' (plus a cross-item condition)';
	return text;
}

/**
 * A run's trigger stamp, in a few words, for the run ledgers: "Entry changed
 * in checklist (2 entries)", "Item updated". `null` when the row carries none.
 */
/** The words for a rule that always runs on a card change (keepr-api KPR-706, `alwaysRunOnCardChange`). */
export const ALWAYS_RUN_LABEL = 'Always runs on a card change';

export function describeRunTrigger( trigger ) {
	if ( ! trigger || typeof trigger !== 'object' ) return null;
	if ( trigger.type === ENTRY_EVENT ) {
		const entries = Array.isArray( trigger.entries ) ? trigger.entries : [];
		const total = entries.length + ( Number( trigger.entriesMore ) || 0 );
		const ops = [ ...new Set( entries.map( e => e?.op ).filter( Boolean ) ) ];
		const where = trigger.group ? ` in ${ trigger.group }` : '';
		// A row whose entries are not named (a condition that failed before
		// any entry was matched) says no more than it knows.
		if ( ! ops.length ) return `Entries${ where }`;
		const positions = entries.map( e => e?.position ).filter( p => Number.isInteger( p ) && p > 0 );
		const places = entries.length <= 3 && positions.length === entries.length
			? ` — entry ${ positions.join( ', ' ) }`
			: '';
		return `Entry ${ joinOr( ops ) }${ where }${ total > 1 ? ` (${ total } entries)` : '' }${ places }`;
	}
	if ( trigger.type === 'item-event' ) {
		// A card change ran it on an item it brought onto the card (keepr-api KPR-706).
		if ( trigger.cardChange === true ) return 'Item changed to this card';
		const event = trigger.event === 'created' ? 'created' : trigger.event === 'deleted' ? 'deleted' : 'updated';
		return `Item ${ event }${ trigger.derived ? ' (recalculated)' : '' }`;
	}
	if ( trigger.type === 'schedule' ) return 'On its schedule';
	return trigger.type ? `${ trigger.type }` : null;
}
